/**
 * SuiviProspection — fiche de suivi terrain d'un bien : statut, dates, occupant, projet du propriétaire, contact, relance, notes.
 * Enregistrée côté serveur (table v2_suivi_prospection) au nom de l'agent connecté.
 * Le décile et la chance de vente du bien sont figés au moment de la première saisie (mesure du rendement par groupe cible / témoin).
 *
 * Props :
 *   source   — 'dvf' (onglet Biens scorés) ou 'v2' (onglet Maisons silencieuses)
 *   cle      — identifiant du bien dans cette source
 *   bien     — { adresse, commune, code_postal, latitude, longitude, decile, score }
 *   value    — suivi existant (ou undefined)
 *   onSaved  — callback(suivi) après enregistrement
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, MenuItem, Button, Chip, Alert } from '@mui/material';
import DirectionsIcon from '@mui/icons-material/Directions';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

export const STATUTS = [
  { value: 'a_prospecter', label: 'À prospecter', color: '#757575', bg: '#f5f5f5' },
  { value: 'prospecte', label: 'Prospecté', color: '#1565c0', bg: '#e3f2fd' },
  { value: 'absent', label: 'Absent, à repasser', color: '#6d4c41', bg: '#efebe9' },
  { value: 'rdv', label: 'Rendez-vous pris', color: '#e65100', bg: '#fff3e0' },
  { value: 'mandat', label: 'Mandat signé', color: '#2e7d32', bg: '#e8f5e9' },
  { value: 'refus', label: 'Refus', color: '#c62828', bg: '#ffebee' },
  { value: 'sans_suite', label: 'Sans suite', color: '#9e9e9e', bg: '#fafafa' },
];
export const OCCUPANTS = [
  { value: 'proprietaire', label: 'Propriétaire occupant' },
  { value: 'locataire', label: 'Locataire (bailleur ailleurs)' },
  { value: 'secondaire', label: 'Résidence secondaire' },
  { value: 'vacant', label: 'Logement vide' },
  { value: 'inconnu', label: 'Inconnu' },
];
export const PROJETS = [
  { value: 'aucun', label: 'Aucun projet de vente' },
  { value: 'reflexion', label: 'En réflexion (plus tard)' },
  { value: '12_mois', label: 'Vente envisagée dans les 12 mois' },
  { value: 'en_vente', label: 'Déjà en vente (autre agence, particulier)' },
  { value: 'vendu', label: 'Vendu récemment' },
  { value: 'inconnu', label: 'Pas d\'information' },
];
export const GROUPES = { cible: { label: 'Cible', bg: '#ffebee', color: '#c62828' }, temoin: { label: '🎲 Témoin', bg: '#ede7f6', color: '#4527a0' }, autre: { label: 'Hors cible', bg: '#f5f5f5', color: '#616161' } };
export const statutConfig = (v) => STATUTS.find(s => s.value === v);
export const formatChance = (p) => (p == null || p <= 0) ? '' : `1 sur ${Math.round(1 / p)}`;

export function directionsUrl(bien) {
  if (bien?.latitude && bien?.longitude) return `https://www.google.com/maps/dir/?api=1&destination=${bien.latitude},${bien.longitude}`;
  const q = [bien?.adresse, bien?.code_postal, bien?.commune].filter(Boolean).join(' ');
  return q ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}` : null;
}

export function SuiviChip({ suivi }) {
  if (!suivi?.statut) return null;
  const cfg = statutConfig(suivi.statut);
  const date = suivi.date_rdv ? new Date(suivi.date_rdv) : (suivi.date_prospection ? new Date(suivi.date_prospection) : null);
  const label = `${cfg?.label || suivi.statut}${date ? ' · ' + date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : ''}`;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <Chip label={label} size="small" sx={{ bgcolor: cfg?.bg, color: cfg?.color, fontWeight: 700, fontSize: '0.68rem', height: 20 }} />
      {suivi.groupe === 'temoin' && <Chip label="🎲" size="small" title="Échantillon témoin" sx={{ bgcolor: GROUPES.temoin.bg, color: GROUPES.temoin.color, fontWeight: 700, fontSize: '0.68rem', height: 20 }} />}
    </Box>
  );
}

const today = () => new Date().toISOString().slice(0, 10);
const toLocalInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16) : '');
const VIDE = { statut: '', date_prospection: '', date_rdv: '', occupant: '', projet: '', relance_le: '', contact_nom: '', contact_tel: '', notes: '' };

export default function SuiviProspection({ source, cle, bien, value, onSaved }) {
  const [form, setForm] = useState(VIDE);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    setForm({
      statut: value?.statut || '',
      date_prospection: value?.date_prospection ? value.date_prospection.slice(0, 10) : '',
      date_rdv: toLocalInput(value?.date_rdv),
      occupant: value?.occupant || '',
      projet: value?.projet || '',
      relance_le: value?.relance_le ? value.relance_le.slice(0, 10) : '',
      contact_nom: value?.contact_nom || '',
      contact_tel: value?.contact_tel || '',
      notes: value?.notes || '',
    });
    setMsg(null);
  }, [cle, value]);

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm(f => {
      const next = { ...f, [k]: v };
      // Un statut « terrain » sans date : on met la date du jour
      if (k === 'statut' && ['prospecte', 'absent', 'rdv', 'refus', 'mandat'].includes(v) && !f.date_prospection) next.date_prospection = today();
      // Absent : proposer une relance dans 15 jours
      if (k === 'statut' && v === 'absent' && !f.relance_le) { const d = new Date(); d.setDate(d.getDate() + 15); next.relance_le = d.toISOString().slice(0, 10); }
      return next;
    });
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const token = localStorage.getItem('token');
      const body = {
        statut: form.statut || null,
        date_prospection: form.date_prospection || null,
        date_rdv: form.date_rdv ? new Date(form.date_rdv).toISOString() : null,
        occupant: form.occupant || null,
        projet: form.projet || null,
        relance_le: form.relance_le || null,
        contact_nom: form.contact_nom || null,
        contact_tel: form.contact_tel || null,
        notes: form.notes || null,
        decile: bien?.decile != null ? Number(bien.decile) : null,
        score: bien?.score != null ? Number(bien.score) : null,
        adresse: bien?.adresse || null, commune: bien?.commune || null, code_postal: bien?.code_postal || null,
      };
      const res = await axios.put(`${API_BASE}/v2/suivi/${source}/${encodeURIComponent(cle)}`, body, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 15000,
      });
      setMsg({ type: 'success', text: 'Suivi enregistré' });
      onSaved && onSaved(res.data);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const itin = directionsUrl(bien);
  const groupe = value?.groupe || (bien?.decile != null ? (Number(bien.decile) <= 3 ? 'cible' : 'autre') : null);
  const decile = value?.decile ?? bien?.decile;
  const chance = formatChance(value?.score ?? bien?.score);

  return (
    <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>Fiche de prospection</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {groupe && GROUPES[groupe] && <Chip size="small" label={GROUPES[groupe].label} sx={{ bgcolor: GROUPES[groupe].bg, color: GROUPES[groupe].color, fontWeight: 700, height: 22 }} />}
          {decile != null && <Chip size="small" label={`Décile ${decile}${chance ? ' · ' + chance : ''}`} sx={{ height: 22 }} />}
          {itin && (
            <Button size="small" variant="outlined" startIcon={<DirectionsIcon />} href={itin} target="_blank" rel="noopener noreferrer"
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              Itinéraire
            </Button>
          )}
        </Box>
      </Box>
      <TextField select size="small" label="Statut" value={form.statut} onChange={set('statut')}>
        <MenuItem value="">—</MenuItem>
        {STATUTS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
      </TextField>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField size="small" type="date" label="Prospecté le" value={form.date_prospection} onChange={set('date_prospection')} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
        <TextField size="small" type="datetime-local" label="Rendez-vous" value={form.date_rdv} onChange={set('date_rdv')} InputLabelProps={{ shrink: true }} sx={{ flex: 1.3 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField select size="small" label="Qui occupe la maison ?" value={form.occupant} onChange={set('occupant')} sx={{ flex: 1 }}>
          <MenuItem value="">—</MenuItem>
          {OCCUPANTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Projet de vente" value={form.projet} onChange={set('projet')} sx={{ flex: 1.2 }}>
          <MenuItem value="">—</MenuItem>
          {PROJETS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField size="small" label="Contact" value={form.contact_nom} onChange={set('contact_nom')} sx={{ flex: 1.3 }} />
        <TextField size="small" label="Téléphone" value={form.contact_tel} onChange={set('contact_tel')} sx={{ flex: 1 }} />
        <TextField size="small" type="date" label="Relancer le" value={form.relance_le} onChange={set('relance_le')} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
      </Box>
      <TextField size="small" label="Notes (motif du projet, travaux, succession, échéance, ce qu'il faut dire à la relance…)" value={form.notes} onChange={set('notes')} multiline minRows={2} maxRows={6} />
      {msg && <Alert severity={msg.type} sx={{ py: 0 }}>{msg.text}</Alert>}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.disabled">
          {value?.updated_at ? `Mis à jour le ${new Date(value.updated_at).toLocaleDateString('fr-FR')}${value.agent ? ' par ' + value.agent.split('@')[0] : ''}` : 'Pas encore de suivi'}
        </Typography>
        <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving} sx={{ textTransform: 'none' }}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </Box>
    </Box>
  );
}
